import clsx from "clsx";
import Button from "components/button/Button";
import { useModal } from "hooks/useModal";
import moment from "moment-jalaali";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FiEdit,
  FiEye,
  FiTrash2,
  FiUsers,
  FiPlus,
  FiCalendar,
  FiMapPin,
  FiBriefcase,
  FiTag,
  FiAward,
  FiStar,
  FiTrendingUp,
  FiGrid,
  FiClock,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useCreateAssessmentMutation } from "reduxFiles/api/assessmentApi";
import { Company } from "types/companyTypes";
import CreateAssessmentModal from "views/company/assessments/CreateAssessmentModal";

type Props = {
  items: Company[];
  loading?: boolean;
  onEdit: (row: Company) => void;
  onDelete: (row: Company) => void;
};

export default function CompanyCards({
  items,
  loading,
  onEdit,
  onDelete,
}: Props) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language;
  const modal = useModal();

  // assessment
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);

  // form
  const [addApi, { isLoading: isAdding }] = useCreateAssessmentMutation();

  const handleSubmit = async (licenseKey: string) => {
    if (!selectedCompany) return;

    try {
      await addApi({
        companyId: selectedCompany.id,
        organizationId: selectedCompany.organization?.id,
        licenseKey: licenseKey,
      }).unwrap();
      modal.closeModal();
      setSelectedCompany(null);
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center rounded-3xl border-2 border-dashed border-gray-200 dark:border-white/10">
        <div className="loader h-8 w-8 text-brand-500" />
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="flex min-h-[200px] flex-col items-center justify-center rounded-3xl border-2 border-dashed border-gray-200 p-12 text-center dark:border-white/10">
        <div className="mb-4 text-5xl">🏢</div>
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
          {t("company.noCompanyTitle")}
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t("company.noCompanyDesc")}
        </p>
      </div>
    );
  }

  return (
    <div className="mt-4 flex flex-col items-center space-y-6">
      {items.map((item, index) => (
        <div
          key={item.id}
          style={{ animationDelay: `${index * 60}ms` }}
          className={clsx(
            "group relative w-full overflow-hidden rounded-2xl md:w-[90%]",
            "border border-gray-200/80 bg-white/90",
            "backdrop-blur-sm",
            "transition-all duration-300 ease-out",
            "hover:border-brand-200/50 hover:shadow-xl hover:shadow-brand-500/5",
            "dark:border-white/5 dark:bg-navy-800/90",
            "animate-fadeIn"
          )}
        >
          {/* Gradient Line */}
          <div className="absolute left-0 right-0 top-0 h-0.5 bg-gradient-to-r from-brand-400 via-brand-500 to-brand-600 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

          <div className="p-5">
            {/* Header Row */}
            <div className="flex flex-wrap items-start justify-between gap-4">
              {/* Left Section - Company Info */}
              <div className="flex flex-1 items-start gap-4">
                {/* Avatar */}
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand-400 to-brand-600 text-lg font-bold text-white shadow-lg shadow-brand-500/20">
                  {item.name.charAt(0).toUpperCase()}
                </div>

                {/* Company Details */}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                      {t("assessment.detail.stats.companyName")} : {item.name}
                    </h3>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    <span className="font-medium text-gray-700 dark:text-gray-300">
                      {t("organization.name")} :
                    </span>{" "}
                    {item.organization?.name ?? "-"}
                  </p>
                </div>
              </div>

              {/* Right Section - Date & Badges */}
              <div className="flex flex-wrap items-center gap-2">
                {/* Created At */}
                <span className="flex items-center gap-1.5 rounded-full bg-gray-200/80 px-3 py-1.5 text-xs text-gray-700 dark:bg-navy-700/50 dark:text-gray-400">
                  <FiCalendar className="h-3.5 w-3.5" />
                  {moment(item.createdAt).format(
                    lang === "fa" ? "jYYYY/jMM/jDD" : "YYYY/MM/DD"
                  )}
                </span>

                {/* Company Size Badge */}
                <span className="flex items-center gap-1.5 rounded-full bg-blue-500/10 px-3 py-1.5 text-xs font-semibold text-blue-600 dark:bg-blue-500/20 dark:text-blue-400">
                  <FiTrendingUp className="h-3.5 w-3.5" />
                  {t(`group.sizes.${item.totalStaffCount?.companySize}`)}
                </span>

                {/* Knowledge Based Badge */}
                {item.isKnowledgeBased && (
                  <span className="flex items-center gap-1.5 rounded-full bg-green-100/80 px-3 py-1.5 text-xs font-medium text-green-700 dark:bg-green-500/20 dark:text-green-400">
                    <FiAward className="h-3.5 w-3.5" />
                    {t("company.knowledgeBase")}
                  </span>
                )}

                {/* Listed Badge */}
                {item.isListed && (
                  <span className="flex items-center gap-1.5 rounded-full bg-blue-100/80 px-3 py-1.5 text-xs font-medium text-blue-700 dark:bg-blue-500/20 dark:text-blue-400">
                    <FiStar className="h-3.5 w-3.5" />
                    {t("company.listed")}
                  </span>
                )}
              </div>
            </div>

            {/* Divider */}
            <div className="my-4 border-t border-gray-200/50 dark:border-white/5" />

            {/* Info Grid - 4 columns */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              <InfoItem
                icon={<FiTag className="h-4 w-4" />}
                label={t("company.fields.nationalId")}
                value={item.nationalId}
              />
              <InfoItem
                icon={<FiMapPin className="h-4 w-4" />}
                label={t("company.fields.province")}
                value={item.province?.name}
              />
              <InfoItem
                icon={<FiBriefcase className="h-4 w-4" />}
                label={
                  item.companyStage
                    ? t("company.fields.companyStage")
                    : t("company.fields.industrialId")
                }
                value={
                  item.companyStage
                    ? t(`company.companyStages.${item.companyStage}`)
                    : item.industrial?.name
                }
              />
              <InfoItem
                icon={<FiGrid className="h-4 w-4" />}
                label={t("company.fields.industrySectorId")}
                value={t(`industrySectors.${item.industrySector?.key}`)}
              />
              <InfoItem
                icon={<FiTag className="h-4 w-4" />}
                label={t("company.fields.companyType")}
                value={t(`company.companyTypes.${item.companyType}`)}
              />
              <InfoItem
                icon={<FiUsers className="h-4 w-4" />}
                label={t("company.fields.totalStaffCount")}
                value={item.totalStaffCount?.total || "-"}
              />
              <InfoItem
                icon={<FiClock className="h-4 w-4" />}
                label={t("company.fields.activityType")}
                value={t(`company.activityTypes.${item.activityType}`)}
              />
              <InfoItem
                icon={<FiCalendar className="h-4 w-4" />}
                label={t("company.fields.foundationYear")}
                value={
                  item.foundationYear
                    ? moment(item.foundationYear).format(
                        lang === "fa" ? "jYYYY" : "YYYY"
                      )
                    : "-"
                }
              />
            </div>

            {/* Divider */}
            <div className="my-4 border-t border-gray-200/50 dark:border-white/5" />

            {/* Actions - Smaller and closer buttons */}
            <div className="flex flex-wrap items-center gap-1.5">
              <Button
                onClick={() => navigate(`/company/companies/${item.id}/view`)}
                size="sm"
                variant="secondary"
                startIcon={<FiEye className="h-3.5 w-3.5" />}
                className="px-3 py-1.5 text-xs"
              >
                {t("buttons.view")}
              </Button>
              <Button
                onClick={() => {
                  setSelectedCompany(item);
                  modal.openModal();
                }}
                size="sm"
                variant="success"
                startIcon={<FiPlus className="h-3.5 w-3.5" />}
                className="px-3 py-1.5 text-xs"
              >
                {t("assessment.addAssessment")}
              </Button>
              <Button
                onClick={() =>
                  navigate(`/company/companies/${item.id}/responding`)
                }
                size="sm"
                startIcon={<FiUsers className="h-3.5 w-3.5" />}
                className="px-3 py-1.5 text-xs"
              >
                {t("buttons.responding")}
              </Button>
              <Button
                onClick={() => onEdit(item)}
                variant="info"
                size="sm"
                startIcon={<FiEdit className="h-3.5 w-3.5" />}
                className="px-3 py-1.5 text-xs"
              >
                {t("buttons.edit")}
              </Button>
              <Button
                onClick={() => onDelete(item)}
                variant="error"
                size="sm"
                startIcon={<FiTrash2 className="h-3.5 w-3.5" />}
                className="px-3 py-1.5 text-xs"
              >
                {t("buttons.delete")}
              </Button>
            </div>
          </div>
        </div>
      ))}

      <CreateAssessmentModal
        isOpen={modal.isOpen}
        onClose={() => {
          setSelectedCompany(null);
          modal.closeModal();
        }}
        onSubmit={handleSubmit}
        loading={isAdding}
        title={t("assessment.addAssessment")}
        company={selectedCompany}
      />
    </div>
  );
}

// Info Item Component
function InfoItem({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value?: string | number | null;
}) {
  return (
    <div className="flex items-start gap-2.5 rounded-xl bg-gray-50/50 p-2.5 transition-colors hover:bg-gray-100/50 dark:bg-navy-700/30 dark:hover:bg-navy-700/50">
      <div className="mt-0.5 text-brand-500 dark:text-brand-300">{icon}</div>
      <div className="min-w-0">
        <p className="text-xs text-gray-700 dark:text-gray-400">{label}</p>
        <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
          {value ?? "-"}
        </p>
      </div>
    </div>
  );
}
